/**
 * Hook para buscar documentos de banco de dados
 */

import { useQuery, useMutation, useQueryClient, QueryClient, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { databasesApi } from '../services/api';
import type {
  DatabaseDocumentResponse,
  CreateDatabaseDocumentRequest,
  DatabaseDocumentTransferRequest,
  ListResponse,
} from '@troia-v3/types';

export interface DatabaseDocumentsFilters {
  type?: 'properties' | 'vehicles' | 'products' | 'services' | 'documents';
  search?: string;
  status?: string;
  [key: string]: any;
}

export interface UseDatabaseDocumentsOptions {
  databaseId: string;
  filters?: DatabaseDocumentsFilters;
  page?: number;
  limit?: number;
  enabled?: boolean;
}

export interface NextRegistrationCode {
  /** `null` quando a base não tem código utilizável ou é sincronizada. */
  sugestao: string | null;
  prefixo: string | null;
  prefixos: Array<{ prefixo: string; quantidade: number; proximo: string }>;
  campo: 'reference' | 'sku' | null;
  baseSincronizada: boolean;
}

/**
 * Próximo código livre para o cadastro (Referência do imóvel, SKU do produto).
 *
 * Só faz sentido em cadastro NOVO — por isso o `enabled`. O tipo do imóvel entra
 * na chave da busca porque a sugestão muda com ele (casa→CA, apartamento→AP).
 */
export const useNextRegistrationCode = (
  databaseId: string | undefined,
  options: { propertyType?: string; enabled?: boolean } = {}
): UseQueryResult<NextRegistrationCode, Error> => {
  const { propertyType, enabled = true } = options;

  return useQuery({
    queryKey: ['database-next-code', databaseId, propertyType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (propertyType) params.set('propertyType', propertyType);
      const query = params.toString();
      const response = await api.get(
        `databases/${databaseId}/next-code${query ? `?${query}` : ''}`
      );
      return response.data.data as NextRegistrationCode;
    },
    enabled: enabled && Boolean(databaseId),
    // A sugestão envelhece: outra pessoa pode cadastrar enquanto esta tela está
    // aberta. Sem cache, cada abertura de formulário pergunta de novo — e a
    // garantia final é a trava do banco, não este número.
    staleTime: 0,
    gcTime: 0,
  });
};

/**
 * Hook para buscar documentos de um banco de dados
 */
export const useDatabaseDocuments = <T = unknown>(
  options: UseDatabaseDocumentsOptions
): UseQueryResult<ListResponse<DatabaseDocumentResponse<T>>, Error> => {
  const { databaseId, filters = {}, page = 1, limit = 20, enabled = true } = options;

  return useQuery({
    queryKey: ['database-documents', databaseId, filters, page, limit],
    queryFn: async () => {
      const params: Record<string, any> = {
        page,
        limit,
        ...filters
      };

      // Construir filters no formato esperado pelo backend
      const filtersParam: Record<string, any> = {};

      Object.keys(filters).forEach((key) => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          filtersParam[key] = filters[key];
        }
      });

      if (Object.keys(filtersParam).length > 0) {
        params.filters = filtersParam;
      }

      const response = await api.get(`databases/${databaseId}/documents`, { params });
      return response.data.data;
    },
    enabled: enabled && !!databaseId,
    staleTime: 30000, // 30 segundos
  });
};

/**
 * Hook para buscar um documento específico por ID
 * Rota backend: GET /api/databases/documents/:id
 */
export const useDatabaseDocument = <T = unknown>(
  documentId: string,
  enabled = true
): UseQueryResult<DatabaseDocumentResponse<T>, Error> => {
  return useQuery({
    queryKey: ['database-document', documentId],
    queryFn: () => databasesApi.getDocumentById<T>(documentId),
    enabled: enabled && !!documentId,
    staleTime: 30000,
  });
};

/**
 * Trilhas (series) já cadastradas na base — datalist do campo "Trilha".
 */
export const useDocumentSeries = (
  databaseId: string | undefined
): UseQueryResult<Array<{ name: string; count: number }>, Error> => {
  return useQuery({
    queryKey: ['database-document-series', databaseId],
    queryFn: () => databasesApi.getDocumentSeries(databaseId!),
    enabled: !!databaseId,
    staleTime: 30000,
  });
};

/**
 * Hook para criar um documento
 */
export const useCreateDatabaseDocument = <T = unknown>() => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDatabaseDocumentRequest<T>) => databasesApi.createDocument<T>(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['database-documents', variables.databaseId] });
      queryClient.invalidateQueries({ queryKey: ['database-document-series'] });
    },
  });
};

/**
 * Hook para atualizar um documento
 */
export const useUpdateDatabaseDocument = <T = unknown>() => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, data }: { documentId: string; data: Partial<T> }) =>
      databasesApi.updateDocument<T>(documentId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['database-documents'] });
      queryClient.invalidateQueries({ queryKey: ['database-document', result.id] });
      queryClient.invalidateQueries({ queryKey: ['database-document-series'] });
    },
  });
};

/**
 * Hook para deletar um documento
 */
export const useDeleteDatabaseDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => databasesApi.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['database-documents'] });
      // A galeria de bases mostra a contagem de registros — sem esta segunda
      // invalidação o número fica velho na tela (defeito atual do excluir).
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    },
  });
};

/**
 * Variáveis das mutations de copiar/mover.
 *
 * `sourceDatabaseId` NÃO vai no corpo da requisição — serve só para invalidar
 * a listagem da base de ORIGEM (no mover os registros somem de lá). Por isso a
 * `mutationFn` monta o corpo campo a campo em vez de repassar o objeto inteiro.
 */
export interface TransferDatabaseDocumentsVariables extends DatabaseDocumentTransferRequest {
  sourceDatabaseId: string;
}

/**
 * Invalidação comum de copiar/mover.
 *
 * A chave da listagem é `['database-documents', databaseId, filters, page, limit]`
 * — invalidar `['database-documents', <id>]` casa POR PREFIXO com todas as
 * combinações de filtro/página daquela base (comportamento padrão do React
 * Query v5). Invalidar só `['database-documents']` também funcionaria, mas
 * derrubaria o cache de todas as outras bases sem necessidade.
 *
 * `['databases']` cobre a galeria (`['databases', query]`) e o dropdown
 * (`['databases', 'dropdown', type]`) — as duas mostram/derivam a contagem de
 * registros, que muda nas duas operações.
 */
const invalidateAfterTransfer = (
  queryClient: QueryClient,
  variables: TransferDatabaseDocumentsVariables
): void => {
  queryClient.invalidateQueries({ queryKey: ['database-documents', variables.sourceDatabaseId] });
  queryClient.invalidateQueries({ queryKey: ['database-documents', variables.targetDatabaseId] });
  queryClient.invalidateQueries({ queryKey: ['databases'] });
};

/**
 * Hook para copiar registros para outra base — e também para "Duplicar aqui",
 * que é a mesma rota com `targetDatabaseId` igual ao `sourceDatabaseId`.
 * Rota backend: POST /api/databases/documents/copy
 */
export const useCopyDatabaseDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: TransferDatabaseDocumentsVariables) =>
      databasesApi.copyDocuments({
        documentIds: variables.documentIds,
        targetDatabaseId: variables.targetDatabaseId,
      }),
    onSuccess: (_result, variables) => {
      invalidateAfterTransfer(queryClient, variables);
    },
  });
};

/**
 * Hook para mover registros para outra base (mesmo `_id`, só troca de base).
 * Rota backend: POST /api/databases/documents/move
 */
export const useMoveDatabaseDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: TransferDatabaseDocumentsVariables) =>
      databasesApi.moveDocuments({
        documentIds: variables.documentIds,
        targetDatabaseId: variables.targetDatabaseId,
      }),
    onSuccess: (_result, variables) => {
      invalidateAfterTransfer(queryClient, variables);
    },
  });
};
